import { useCallback, useEffect, useState } from "react";
import { Check, EyeOff, ExternalLink, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { call, getJson, mediaUrl } from "@/lib/api";
import { postJson } from "@/lib/auth";

import { SourceFlag } from "@/components/news/NewsCard";

const when = (iso) => (iso ? new Date(iso).toLocaleString("es-MX") : "nunca");

const Row = ({ n, onAction }) => (
  <div className="flex flex-wrap items-start gap-4 border-b border-white/10 py-4" data-testid={`admin-news-row-${n.id}`}>
    {n.image_url ? <img src={mediaUrl(n.image_url)} alt="" className="h-20 w-28 shrink-0 rounded-lg object-cover" /> : <div className="h-20 w-28 shrink-0 rounded-lg bg-white/5" />}
    <div className="min-w-0 flex-1">
      <p className="mono text-[11px] uppercase tracking-[0.14em] text-[var(--muted)]">
        <SourceFlag country={n.country} /> {n.source} · {new Date(n.published_at).toLocaleDateString("es-MX")}
        <span className={`ml-2 rounded-full px-2 py-0.5 text-[#0d0c0a] ${n.status === "published" ? "bg-[var(--green)]" : "bg-[#e8b64a]"}`}>{n.status === "published" ? "Publicada" : "Borrador"}</span>
      </p>
      <p className="font-display mt-1 text-xl">{n.title}</p>
      <p className="mt-1 line-clamp-2 text-sm text-[var(--muted)]">{n.summary}</p>
      <a href={n.url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-[var(--green)]" data-testid={`admin-news-source-${n.id}`}>Original: {n.original_title} <ExternalLink size={12} /></a>
    </div>
    <div className="flex gap-2">
      {n.status === "published" ? (
        <button type="button" onClick={() => onAction(n, "draft")} className="btn-ghost !px-3 !py-2" title="Ocultar" data-testid={`admin-news-hide-${n.id}`}><EyeOff size={14} /></button>
      ) : (
        <button type="button" onClick={() => onAction(n, "published")} className="btn-primary !px-3 !py-2" data-testid={`admin-news-approve-${n.id}`}><Check size={14} /> Publicar</button>
      )}
      <button type="button" onClick={() => onAction(n, "delete")} className="btn-ghost !px-3 !py-2" title="Descartar" data-testid={`admin-news-delete-${n.id}`}><Trash2 size={14} /></button>
    </div>
  </div>
);

export const AdminNews = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("draft");
  const load = useCallback(() => getJson("/admin/news").then(setData), []);
  useEffect(() => {
    load();
  }, [load]);
  const running = !!data?.fetch?.running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, load]);
  const fetchNow = async () => {
    await postJson("/admin/news/fetch", null);
    toast.success("Buscando noticias… tarda uno o dos minutos");
    setData((d) => ({ ...d, fetch: { ...d.fetch, running: true } }));
  };
  const action = async (n, what) => {
    if (what === "delete") {
      if (!window.confirm("¿Descartar esta noticia? No volverá a aparecer en las búsquedas.")) return;
      await call(`/admin/news/${n.id}`, { method: "DELETE" });
    } else await postJson(`/admin/news/${n.id}`, { status: what }, "PATCH");
    load();
  };
  if (!data) return <Loader2 className="mx-auto mt-10 animate-spin" />;
  const items = data.items.filter((n) => filter === "all" || n.status === filter);
  const count = (s) => data.items.filter((n) => n.status === s).length;
  return (
    <div data-testid="admin-news">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-[var(--card)] p-5">
        <div>
          <p className="font-display text-2xl">Noticias</p>
          <p className="mt-1 text-sm text-[var(--muted)]" data-testid="admin-news-status">
            Se busca automáticamente una vez al día. Última búsqueda: {when(data.fetch?.last_run)}
            {data.fetch?.last_run && ` · ${data.fetch.last_added ?? 0} nuevas`}
            {data.fetch?.last_error && <span className="text-[#ff8a7a]"> · Error: {data.fetch.last_error}</span>}
          </p>
        </div>
        <button type="button" onClick={fetchNow} disabled={running} className="btn-primary disabled:opacity-60" data-testid="admin-news-fetch-btn">
          <RefreshCw size={15} className={running ? "animate-spin" : ""} /> {running ? "Buscando…" : "Buscar ahora"}
        </button>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {[["draft", `Borradores (${count("draft")})`], ["published", `Publicadas (${count("published")})`], ["all", "Todas"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} className={`col-chip ${filter === k ? "col-chip-on" : ""}`} data-testid={`admin-news-filter-${k}`}>{l}</button>
        ))}
      </div>
      {!items.length && <p className="mt-6 text-sm text-[var(--muted)]" data-testid="admin-news-empty">No hay noticias en esta lista.</p>}
      <div className="mt-2">{items.map((n) => <Row key={n.id} n={n} onAction={action} />)}</div>
    </div>
  );
};
